import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Toaster } from 'sonner';
import { Loader2, ArrowLeft, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import ReportView from '../components/assessment/report/ReportView';
import { getAttemptReport, getSharedAssessmentReport, apiError } from '../services/assessmentApi';

// Polls until AI feedback is ready
function useReport(fetcher, id) {
  const [report, setReport] = useState(null);
  const [error, setError] = useState('');
  useEffect(() => {
    let timer;
    let alive = true;
    const load = async () => {
      try {
        const r = await fetcher(id);
        if (!alive) return;
        setReport(r);
        if (r.ai_status === 'pending' || r.lp_status === 'pending') timer = setTimeout(load, 5000);
      } catch (e) {
        if (alive) setError(apiError(e, 'Report not found'));
      }
    };
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [fetcher, id]);
  return { report, error };
}

const State = ({ error }) => (
  <div className="flex flex-col items-center justify-center py-24 gap-3 text-slate-500">
    {error ? <><AlertTriangle className="h-8 w-8 text-red-500" /><p data-testid="report-error">{error}</p></> : <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />}
  </div>
);

export function SharedAssessmentReportPage() {
  const { shareToken } = useParams();
  const { report, error } = useReport(getSharedAssessmentReport, shareToken);
  return (
    <div className="min-h-screen bg-[#F7F8FB]">
      <div className="h-1 bg-gradient-to-r from-indigo-600 via-indigo-500 to-emerald-600" />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8">{report ? <ReportView report={report} /> : <State error={error} />}</main>
      <Toaster richColors position="top-center" />
    </div>
  );
}

export default function AssessmentReportPage() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  const { report, error } = useReport(getAttemptReport, attemptId);
  return (
    <div className="p-6 lg:p-8 max-w-6xl mx-auto space-y-4">
      <Button variant="ghost" className="gap-2 -ml-2" onClick={() => navigate(-1)} data-testid="report-back-btn"><ArrowLeft className="h-4 w-4" /> Back to results</Button>
      <div className="rounded-3xl bg-[#F7F8FB] p-4 sm:p-6 border">{report ? <ReportView report={report} embedded /> : <State error={error} />}</div>
    </div>
  );
}
